import { z } from 'zod'
import {
  BookSize,
  BookTextStyle,
  designedCovers,
  type DesignedCoverId,
  FILTERS,
  FRAME_COLORS,
  MATTING,
  MATERIAL_TYPES,
  ProductIds,
  TILE_SIZES,
} from '@mixtiles/web-backend-shared'
import { PRODUCT_TYPES } from 'services/ProductTypeState'
import { TileWarningTypeSchema } from 'pages/PhotoStyler/TileWarning/tileWarning.consts'
import { PhotoBookWarningTypeSchema } from 'components/PhotoBook/photoBookWarning.consts'

export const CART_STORAGE_KEY = 'cart'
export const CART_SCHEMA_VERSION = 1
export const TILES_CART_ITEM_ID = 'classic-tiles'

export const TileCartItemWarningSchema = z.object({
  type: TileWarningTypeSchema,
  tileId: z.string(),
})

export const PhotoBookCartItemWarningSchema = z.object({
  type: PhotoBookWarningTypeSchema,
  missingPhotos: z.number().optional(),
})

const BaseCartMetadataSchema = z.object({
  version: z.literal(1),
  updatedAt: z.number(),
})

// A crop expressed as fractions (0-1) of the source image dimensions.
const ThumbCropSchema = z.object({
  xRatio: z.number(),
  yRatio: z.number(),
  widthRatio: z.number(),
  heightRatio: z.number(),
})

// Full-image crop: thumbnails always carry a crop, defaulting to this when the
// source has none, so render components never deal with a missing crop.
export const DEFAULT_THUMB_CROP = {
  xRatio: 0,
  yRatio: 0,
  widthRatio: 1,
  heightRatio: 1,
}

const TilesThumbDataSchema = z.object({
  thumbUrl: z.string().nullable(),
  crop: ThumbCropSchema.default(DEFAULT_THUMB_CROP),
  filterImageUrl: z.string().optional(),
  materialType: z.nativeEnum(MATERIAL_TYPES),
  frameColor: z.nativeEnum(FRAME_COLORS),
  matting: z.nativeEnum(MATTING),
  tileSize: z.nativeEnum(TILE_SIZES),
  filter: z.nativeEnum(FILTERS),
  mattingColor: z.string(),
})

const PhotoBookThumbDataSchema = z.object({
  thumbUrl: z.string().nullable(),
  crop: ThumbCropSchema.default(DEFAULT_THUMB_CROP),
  coverText: z.string().nullable(),
  coverTextStyle: z.nativeEnum(BookTextStyle).optional(),
  // Rendered title font-size (inches), computed by the cover editor and
  // reused to size the cart thumbnail's title proportionally.
  coverFontHeight: z.number().optional(),
})

const BaseCartItemSchema = z.object({
  id: z.string(),
  quantity: z.number(),
  productType: z.string(),
  warnings: z.array(z.object({ type: z.string() }).passthrough()).optional(),
  dismissedWarningTypes: z.array(z.string()).optional(),
})

const TilesPricingProductSchema = z.object({
  id: z.literal(ProductIds.SKU_TILES),
  pricingProduct: z.object({
    tileSize: z.nativeEnum(TILE_SIZES),
    materialType: z.nativeEnum(MATERIAL_TYPES).optional(),
    frameColor: z.nativeEnum(FRAME_COLORS).optional(),
  }),
  quantity: z.number(),
})

export const TilesCartItemSchema = BaseCartItemSchema.extend({
  productType: z.literal(PRODUCT_TYPES.CLASSIC),
  warnings: z.array(TileCartItemWarningSchema).optional(),
  metadata: BaseCartMetadataSchema.extend({
    thumb: TilesThumbDataSchema.nullable(),
    backgroundThumb: TilesThumbDataSchema.nullable(),
    tilesCount: z.number(),
    tileSizes: z.preprocess(
      (val) => {
        if (Array.isArray(val)) {
          const record: Record<string, number> = {}
          for (const size of val) {
            record[size as string] = (record[size as string] ?? 0) + 1
          }
          return record
        }
        return val
      },
      z.record(z.nativeEnum(TILE_SIZES), z.number())
    ),
    centerpiecesCount: z.number().default(0),
    materialTypes: z.array(z.nativeEnum(MATERIAL_TYPES)),
    frameColors: z.array(z.nativeEnum(FRAME_COLORS)),
    pricingProducts: z.array(TilesPricingProductSchema),
  }),
})

// A photo book has exactly one cover: a photo, designed, or AI-generated cover.
// We discriminate between them by type.
const PhotoCoverSchema = z.object({
  type: z.literal('photo'),
  thumb: PhotoBookThumbDataSchema,
})
const DesignedCoverSchema = z.object({
  type: z.literal('designed'),
  designedCoverId: z.enum(
    Object.keys(designedCovers) as [DesignedCoverId, ...DesignedCoverId[]]
  ),
})
// AI covers render the headline into the generated image, so the cart only
// needs the image as a thumb (no overlaid title).
const AiGeneratedCoverSchema = z.object({
  type: z.literal('ai-generated'),
  thumb: PhotoBookThumbDataSchema,
})
const PhotoBookCoverSchema = z.discriminatedUnion('type', [
  PhotoCoverSchema,
  DesignedCoverSchema,
  AiGeneratedCoverSchema,
])

export const PhotoBookCartItemSchema = BaseCartItemSchema.extend({
  productType: z.literal(PRODUCT_TYPES.PHOTO_BOOK),
  warnings: z.array(PhotoBookCartItemWarningSchema).optional(),
  metadata: BaseCartMetadataSchema.extend({
    size: z.nativeEnum(BookSize),
    pageCount: z.number(),
    photosCount: z.number(),
    cover: PhotoBookCoverSchema,
  }),
})

export const CartItemSchema = z.discriminatedUnion('productType', [
  TilesCartItemSchema,
  PhotoBookCartItemSchema,
])

export const CartStorageSchema = z.object({
  schemaVersion: z.literal(1),
  items: z.array(CartItemSchema),
  updatedAt: z.number(),
})

export type BaseCartMetadata = z.infer<typeof BaseCartMetadataSchema>
export type CartStorage = z.infer<typeof CartStorageSchema>
export type CartItem = z.infer<typeof CartItemSchema>
export type TilesCartItem = z.infer<typeof TilesCartItemSchema>
export type PhotoBookCartItem = z.infer<typeof PhotoBookCartItemSchema>
export type ThumbCrop = z.infer<typeof ThumbCropSchema>
export type TileCartItemWarning = z.infer<typeof TileCartItemWarningSchema>
export type PhotoBookCartItemWarning = z.infer<
  typeof PhotoBookCartItemWarningSchema
>
export type IncompleteCollageCartItemWarning = TileCartItemWarning & {
  type: 'incomplete-collage'
}
export type AiTileGeneratingCartItemWarning = TileCartItemWarning & {
  type: 'ai-tile-generating'
}
export type NotEnoughPhotosCartItemWarning = PhotoBookCartItemWarning & {
  type: 'not-enough-photos'
}
export type TilesThumbData = z.infer<typeof TilesThumbDataSchema>
